import { bound } from "@frui.ts/helpers";
import { BusyWatcher, ScreenBase } from "@frui.ts/screens";
import { action, computed, observable } from "mobx";
import ChargeManagementService, {
  CHANGE_NOT_ALLOWED_ANOTHER_CHARGE_FOR_VEHICLE_WITH_VALIDITY_OVERLAP_EXISTS,
  WEBSOCKET_CONNECTION_FAILURE,
} from "services/chargeManagementService";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import { scrollToFirstError } from "utils/scrollTo";
import { IChargeManagementEditPartViewModel } from "./types";
import React from "react";
import EnumService from "services/enumService";
import ExemptionReasonResponse from "entities/exemptionReasonResponse";
import ChargeOverlapResponse from "entities/chargeOverlapResponse";
import { TurnstileInstance } from "@marsidev/react-turnstile";
import TurnstileViewModel from "../../common/turnstileViewModel";

export default abstract class ChargeManagementEditPartViewModel extends ScreenBase implements IChargeManagementEditPartViewModel {
  onRecapchaBeforeInteractive: any;
  public abstract localizationService: LocalizationService;
  protected abstract chargeManagementService: ChargeManagementService;
  protected abstract enumService: EnumService;
  protected abstract eventBus: EventBus;
  protected abstract signalRNotificationService: SignalRNotificationService;
  @observable formElement?: HTMLFormElement;
  @observable isLoading = false;
  abstract turnstileViewModel: TurnstileViewModel;

  public abstract validate(): boolean;
  abstract submit(): void;
  abstract get canEdit(): boolean;
  abstract get hasSecondStep(): boolean;
  protected unsubscriptions: Array<() => void> = [];
  @observable collapsed = true;
  @observable success = false;
  @observable secondStepLoading = false;
  @observable showSecondStep = false;
  @observable hideConfirmChangeButton = false;
  @observable warningMessages = [] as string[];
  @observable secondStepErrorMessage?: string;

  @observable errorMessage?: string;
  busyWatcher: BusyWatcher = new BusyWatcher();

  get canSubmit() {
    // Overload if need custom logic
    return true;
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  @bound
  translate(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`charge.management.edit.${code}`, undefined, disableUnbreakableRules);
  }

  @bound
  translateWarningReason(code: string, possibleExemptionReasonIds?: string[]): string {
    if (code === "IDENTIFIED_POSSIBLE_EXEMPTION") {
      if (!possibleExemptionReasonIds || possibleExemptionReasonIds.length < 1) {
        return this.localizationService.translateGeneral("errors.generic");
      }
      const possibleExemptionReasonId = possibleExemptionReasonIds[0];
      const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
      const matchingReason = exemptionReasons.find(x => x.id === possibleExemptionReasonId);
      switch (matchingReason?.exemptionReason) {
        case "C":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_C", possibleExemptionReasonIds);
        case "P":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_P", possibleExemptionReasonIds);
        case "O":
        case "O1":
        case "O2":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_O", possibleExemptionReasonIds);
        default:
          return this.localizationService.translateGeneral("errors.generic");
      }
    }
    return this.localizationService.translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`, undefined, false);
  }

  @bound
  translateOverlapReason(code: string): string {
    return this.localizationService.translateGeneral(`eshop.order.license.overlapWarnings.${code}`, undefined, false);
  }

  @bound
  translateOverlappingCharge(chargeOverlap: ChargeOverlapResponse, isDifferentTimeZone: boolean) {
    return {
      validSince: chargeOverlap.validSince,
      validUntil: chargeOverlap.validUntil,
      isDifferentTimeZone: isDifferentTimeZone,
      formatDateTime: this.localizationService.formatDateTime,
      translateGeneral: this.localizationService.translateGeneral,
    };
  }

  @bound
  toggle() {
    this.collapsed = !this.collapsed;
  }

  @bound
  resetSecondStep() {
    this.showSecondStep = false;
    this.hideConfirmChangeButton = false;
    this.secondStepErrorMessage = undefined;
    this.warningMessages = [];
  }

  @bound
  setEmailAddress(emailAddress: string) {
    if (this.charge?.emailAddress !== emailAddress) {
      this.charge!.emailAddress = emailAddress;
    }
    if (this.charge?.notificationPreferences.emailAddress !== emailAddress) {
      this.charge!.notificationPreferences.emailAddress = emailAddress;
    }
  }

  @computed
  get charge() {
    return this.chargeManagementService.charge;
  }

  @action
  protected clearAfterAsyncAction = () => {
    this.signalRNotificationService.serviceStop();
  };

  @action.bound
  protected setErrorMessageBasedOnType(type: string) {
    if (type === WEBSOCKET_CONNECTION_FAILURE) {
      this.errorMessage = this.websocketError;
    } else if (type === CHANGE_NOT_ALLOWED_ANOTHER_CHARGE_FOR_VEHICLE_WITH_VALIDITY_OVERLAP_EXISTS) {
      this.errorMessage = this.changeNotAllowedAnotherChargeForVehicleWithValidityOverlapExistsError;
      scrollToFirstError(this.formElement);
    } else {
      this.errorMessage = this.genericError;
    }
  }

  @computed
  get genericError() {
    return this.localizationService.translateGeneral("errors.generic");
  }

  @computed
  get changeNotAllowedAnotherChargeForVehicleWithValidityOverlapExistsError() {
    return this.translate("changeNotAllowedAnotherChargeForVehicleWithValidityOverlapExists");
  }

  @computed
  get websocketError() {
    return this.localizationService.translateGeneral("errors.websocket");
  }

  @computed
  get emailInputRequired() {
    return this.charge && !this.charge.emailAddress;
  }

  @action.bound
  protected reset() {
    this.collapsed = true;
    this.success = false;
    this.errorMessage = undefined;
  }
}
